import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { onAuthStateChanged, getAuth } from 'firebase/auth';
import { getMascotUserData, markMessageSeen } from './mascotFirestore';
import { pickMessage, renderMessageText } from './pickMessage';

const AUTO_TRIGGER_COOLDOWN_MS = 4 * 60 * 60 * 1000; // don't auto-show more than once per 4h
const AUTO_TRIGGER_CHANCE = 0.4; // chance it fires at all, once cooldown has passed
const AUTO_CENTER_CHANCE = 0.15; // of the auto-triggers that fire, how many are "big pull" center takeovers

const MascotContext = createContext(null);

export function MascotProvider({ children }) {
  const [uid, setUid] = useState(null);
  const [authorName, setAuthorName] = useState('');
  const seenIdsRef = useRef([]);
  const lastShownRef = useRef(null);
  const hasCheckedAutoTrigger = useRef(false);

  const [mascotState, setMascotState] = useState({
    visible: false,
    template: 'corner',
    text: '',
    messageId: null,
  });

  // Load the user's mascot-relevant data once they're known
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setUid(null);
        return;
      }
      setUid(user.uid);
      const data = await getMascotUserData(user.uid);
      setAuthorName(data.authorName);
      seenIdsRef.current = data.seenMessageIds;
      lastShownRef.current = data.mascotLastShown;
    });
    return unsubscribe;
  }, []);

  const presentMessage = useCallback(
    (template) => {
      const message = pickMessage(template, seenIdsRef.current);
      if (!message) return;
      setMascotState({
        visible: true,
        template,
        text: renderMessageText(message, authorName),
        messageId: message.id,
      });
    },
    [authorName]
  );

  // Manual trigger — e.g. tapping the mascot icon
  const showMascot = useCallback(
    (template = 'corner') => {
      presentMessage(template);
    },
    [presentMessage]
  );

  const dismissMascot = useCallback(() => {
    setMascotState((prev) => {
      if (prev.messageId && uid) {
        seenIdsRef.current = [...seenIdsRef.current, prev.messageId];
        markMessageSeen(uid, prev.messageId);
      }
      return { ...prev, visible: false };
    });
  }, [uid]);

  // Random auto-trigger — checked once per session, after user data has loaded
  useEffect(() => {
    if (!uid || hasCheckedAutoTrigger.current) return;
    hasCheckedAutoTrigger.current = true;

    const now = Date.now();
    const cooledDown = !lastShownRef.current || now - lastShownRef.current > AUTO_TRIGGER_COOLDOWN_MS;
    if (!cooledDown) return;
    if (Math.random() > AUTO_TRIGGER_CHANCE) return;

    const template = Math.random() < AUTO_CENTER_CHANCE ? 'center' : 'corner';
    presentMessage(template);
  }, [uid, presentMessage]);

  return (
    <MascotContext.Provider value={{ mascotState, showMascot, dismissMascot }}>
      {children}
    </MascotContext.Provider>
  );
}

export function useMascot() {
  const ctx = useContext(MascotContext);
  if (!ctx) throw new Error('useMascot must be used inside a MascotProvider');
  return ctx;
}